import React, { useState, useEffect, useCallback } from 'react';
import { PageHeader, MetricCard, SectionHeader, ModernCard } from '@/components/ModernUI';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import DataSyncStatus from '@/components/DataSyncStatus';
import PredictiveFeatures from '@/components/PredictiveFeatures';
import DataRefreshIndicator from '@/components/common/DataRefreshIndicator';
import {
  Upload,
  FileSpreadsheet,
  AlertTriangle,
  CheckCircle,
  Users,
  TrendingUp,
  Brain,
  Zap,
  RefreshCw,
  Download,
  PlayCircle,
  FileText,
  Clock,
  AlertCircle,
  ArrowRight,
  Building2,
  UserPlus,
  Activity,
  Flame,
  Shield,
  ChevronRight,
  DollarSign,
  UserX,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function CareFlowIntelligence() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [runningAllSims, setRunningAllSims] = useState(false);
  
  // Analysis data
  const [analysisData, setAnalysisData] = useState(null);
  const [executiveReport, setExecutiveReport] = useState(null);
  const [burnoutAlerts, setBurnoutAlerts] = useState([]);
  const [teamCapacity, setTeamCapacity] = useState(null);
  const [hiringRecommendations, setHiringRecommendations] = useState(null);
  const [reports, setReports] = useState([]);
  const [dataSources, setDataSources] = useState([]);
  const [operationsBrief, setOperationsBrief] = useState(null);
  
  // Upload dialog
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('qapi-token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }, []);

  const fetchAnalysisData = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/careflow/analyze`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setAnalysisData(data);
        // Store data sources for display
        if (data.data_sources_detail) {
          setDataSources(data.data_sources_detail);
        }
      }
    } catch (error) {
      console.error('Error fetching analysis:', error);
    }
  }, [getAuthHeaders]);

  const fetchBurnoutAlerts = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/careflow/burnout-alerts`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setBurnoutAlerts(data.alerts || []);
      }
    } catch (error) {
      console.error('Error fetching burnout alerts:', error);
    }
  }, [getAuthHeaders]);

  const fetchTeamCapacity = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/careflow/team-capacity`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setTeamCapacity(data);
      }
    } catch (error) {
      console.error('Error fetching team capacity:', error);
    }
  }, [getAuthHeaders]);

  const fetchHiringRecommendations = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/careflow/hiring-recommendations`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setHiringRecommendations(data);
      }
    } catch (error) {
      console.error('Error fetching hiring recommendations:', error);
    }
  }, [getAuthHeaders]);

  const fetchReports = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/careflow/reports`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setReports(data.reports || []);
      }
    } catch (error) {
      console.error('Error fetching reports:', error);
    }
  }, [getAuthHeaders]);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    await Promise.all([
      fetchAnalysisData(),
      fetchBurnoutAlerts(),
      fetchTeamCapacity(),
      fetchHiringRecommendations(),
      fetchReports(),
    ]);
    setLoading(false);
  }, [fetchAnalysisData, fetchBurnoutAlerts, fetchTeamCapacity, fetchHiringRecommendations, fetchReports]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const handleFileUpload = async () => {
    if (!selectedFile) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);
    
    try {
      const token = localStorage.getItem('qapi-token');
      const response = await fetch(`${API_URL}/api/careflow/upload-schedule`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });
      
      if (response.ok) {
        const result = await response.json();
        toast({
          title: 'Schedule Uploaded',
          description: `${result.records_processed} records processed successfully`,
        });
        setUploadDialogOpen(false);
        setSelectedFile(null);
        await fetchAllData();
      } else {
        const error = await response.json();
        toast({
          title: 'Upload Failed',
          description: error.detail || 'Failed to upload schedule',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Upload Error',
        description: 'Network error during upload',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  const runWeeklyAnalysis = async () => {
    setAnalyzing(true);
    try {
      const response = await fetch(`${API_URL}/api/careflow/run-weekly-analysis`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      
      if (response.ok) {
        const result = await response.json();
        toast({
          title: 'Analysis Complete',
          description: `Report generated with ${result.recommendations_count} recommendations`,
        });
        await fetchAllData();
        
        // Also fetch executive report
        const reportResponse = await fetch(`${API_URL}/api/careflow/executive-report`, {
          headers: getAuthHeaders(),
        });
        if (reportResponse.ok) {
          const reportData = await reportResponse.json();
          setExecutiveReport(reportData.report);
        }
      } else {
        toast({
          title: 'Analysis Failed',
          description: 'Unable to run weekly analysis',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Analysis Error',
        description: 'Network error during analysis',
        variant: 'destructive',
      });
    } finally {
      setAnalyzing(false);
    }
  };

  const syncDataNow = async () => {
    setSyncing(true);
    try {
      const response = await fetch(`${API_URL}/api/careflow/sync-now`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      
      if (response.ok) {
        const result = await response.json();
        toast({
          title: 'Data Synced',
          description: `Synced ${result.summary.nurses_synced} nurses from ${result.data_sources.length} sources`,
        });
        await fetchAllData();
      } else {
        toast({
          title: 'Sync Failed',
          description: 'Unable to sync data from modules',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Sync Error',
        description: 'Network error during sync',
        variant: 'destructive',
      });
    } finally {
      setSyncing(false);
    }
  };

  const runAllSimulations = async () => {
    setRunningAllSims(true);
    try {
      const response = await fetch(`${API_URL}/api/careflow/simulations/run-all`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      
      if (response.ok) {
        const result = await response.json();
        setOperationsBrief(result.operations_brief);
        toast({
          title: 'Monday Operations Brief Generated',
          description: `${result.recommendations?.length || 0} recommendations across 5 simulations`,
        });
        // Switch to the new Operations Brief tab
        setActiveTab('operations-brief');
      } else {
        toast({
          title: 'Simulation Failed',
          description: 'Unable to run all simulations',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Simulation Error',
        description: 'Network error during simulation',
        variant: 'destructive',
      });
    } finally {
      setRunningAllSims(false);
    }
  };

  const fetchLatestBrief = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/careflow/operations-brief/latest`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        if (data.brief) {
          setOperationsBrief(data.brief.operations_brief);
        }
      }
    } catch (error) {
      console.error('Error fetching operations brief:', error);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchLatestBrief();
  }, [fetchLatestBrief]);

  const getBurnoutColor = (score) => {
    if (score >= 5) return 'bg-red-500';
    if (score >= 4) return 'bg-orange-500';
    if (score >= 3) return 'bg-yellow-500';
    if (score >= 2) return 'bg-blue-500';
    return 'bg-green-500';
  };

  const getRiskBadge = (riskLevel) => {
    const variants = {
      'Critical': 'destructive',
      'High Risk': 'destructive',
      'Moderate': 'warning',
      'Watch': 'secondary',
      'Stable': 'default',
    };
    return variants[riskLevel] || 'default';
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-32 w-full" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="careflow-intelligence-page">
      <PageHeader
        title="CareFlow Intelligence"
        subtitle="AI-Powered Monday Workforce & Capacity Optimization"
        gradient="from-violet-600 to-purple-600"
        actions={
          <div className="flex gap-2">
            <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="gap-2" data-testid="upload-schedule-btn">
                  <Upload className="w-4 h-4" />
                  Upload Schedule
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Upload Nurse Schedule</DialogTitle>
                  <DialogDescription>
                    Upload a CSV or Excel file with your weekly nurse schedule data.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="border-2 border-dashed rounded-lg p-6 text-center">
                    <FileSpreadsheet className="w-12 h-12 mx-auto text-muted-foreground mb-2" />
                    <Input
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={(e) => setSelectedFile(e.target.files?.[0])}
                      className="max-w-xs mx-auto"
                      data-testid="file-input"
                    />
                    {selectedFile && (
                      <p className="mt-2 text-sm text-muted-foreground">
                        Selected: {selectedFile.name}
                      </p>
                    )}
                  </div>
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      Expected columns: Nurse Name, Role, Team, Patient Name, Visit Type, Visit Date, Caseload
                    </AlertDescription>
                  </Alert>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleFileUpload} 
                    disabled={!selectedFile || uploading}
                    data-testid="upload-submit-btn"
                  >
                    {uploading ? 'Uploading...' : 'Upload & Analyze'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            
            <Button 
              onClick={syncDataNow} 
              disabled={syncing}
              variant="outline"
              className="gap-2"
              data-testid="sync-data-btn"
            >
              {syncing ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Syncing...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4" />
                  Sync Data
                </>
              )}
            </Button>
            
            <Button 
              onClick={runWeeklyAnalysis} 
              disabled={analyzing}
              className="gap-2 bg-violet-600 hover:bg-violet-700"
              data-testid="run-analysis-btn"
            >
              {analyzing ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <PlayCircle className="w-4 h-4" />
                  Run Weekly Analysis
                </>
              )}
            </Button>
            
            <Button 
              onClick={runAllSimulations} 
              disabled={runningAllSims}
              className="gap-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
              data-testid="run-all-sims-btn"
            >
              {runningAllSims ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Generating Brief...
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4" />
                  Monday Operations Brief
                </>
              )}
            </Button>
          </div>
        }
      />

      {/* Data Sources Banner */}
      {dataSources.length > 0 && (
        <Alert className="bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20 border-violet-200">
          <Zap className="h-4 w-4 text-violet-500" />
          <AlertTitle className="text-violet-700 dark:text-violet-300 flex items-center justify-between">
            <span>Auto-Synced Data Sources</span>
            <DataRefreshIndicator compact onRefresh={fetchAllData} />
          </AlertTitle>
          <AlertDescription className="text-violet-600 dark:text-violet-400">
            {dataSources.join(' • ')}
          </AlertDescription>
        </Alert>
      )}

      {/* Data Sync Status */}
      <DataSyncStatus showFixButton={true} />

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <MetricCard
          title="Total Nurses"
          value={analysisData?.total_nurses || 0}
          icon={Users}
          gradient="teal"
        />
        <MetricCard
          title="Avg Capacity"
          value={`${analysisData?.avg_capacity_utilization || 0}%`}
          icon={Activity}
          gradient={analysisData?.avg_capacity_utilization > 85 ? 'rose' : 'emerald'}
          subtitle={analysisData?.avg_capacity_utilization > 85 ? 'Above threshold' : 'Healthy'}
        />
        <MetricCard
          title="Burnout Alerts"
          value={burnoutAlerts?.length || 0}
          icon={Flame}
          gradient={burnoutAlerts?.length > 0 ? 'amber' : 'green'}
          subtitle={`${burnoutAlerts?.filter(b => b.burnout_score >= 4).length || 0} critical`}
        />
        <MetricCard
          title="Hiring Urgency"
          value={hiringRecommendations?.hiring_urgency || 'Low'}
          icon={UserPlus}
          gradient={hiringRecommendations?.hiring_urgency === 'critical' ? 'rose' : 'blue'}
        />
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-white/50 dark:bg-slate-800/50">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="operations-brief" data-testid="tab-operations-brief" className="gap-1">
            <Zap className="w-3 h-3" />
            Operations Brief
          </TabsTrigger>
          <TabsTrigger value="predictive" data-testid="tab-predictive" className="gap-1">
            <TrendingUp className="w-3 h-3" />
            Predictive Analytics
          </TabsTrigger>
          <TabsTrigger value="nurses" data-testid="tab-nurses">Nurse Analysis</TabsTrigger>
          <TabsTrigger value="teams" data-testid="tab-teams">Team Capacity</TabsTrigger>
          <TabsTrigger value="recommendations" data-testid="tab-recommendations">Recommendations</TabsTrigger>
          <TabsTrigger value="ai-insights" data-testid="tab-ai-insights">AI Insights</TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-reports">Reports</TabsTrigger>
        </TabsList>

        {/* Operations Brief Tab */}
        <TabsContent value="operations-brief" className="space-y-4">
          {operationsBrief ? (
            <div className="space-y-4">
              {/* Executive Summary Card */}
              <ModernCard className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2 text-2xl">
                        <Zap className="w-6 h-6 text-amber-500" />
                        Monday Operations Brief
                      </CardTitle>
                      <CardDescription>{operationsBrief.report_date}</CardDescription>
                    </div>
                    <Badge 
                      variant={operationsBrief.executive_summary?.health_color === 'green' ? 'default' : 
                               operationsBrief.executive_summary?.health_color === 'yellow' ? 'warning' : 'destructive'}
                      className="text-lg px-4 py-2"
                    >
                      {operationsBrief.executive_summary?.health_status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-xl font-medium">{operationsBrief.executive_summary?.headline}</p>
                  <div className="grid grid-cols-4 gap-4 mt-4">
                    <div className="text-center p-3 bg-white/50 dark:bg-slate-800/50 rounded-lg">
                      <p className="text-2xl font-bold">{operationsBrief.executive_summary?.key_metrics?.total_nurses}</p>
                      <p className="text-xs text-muted-foreground">Total Nurses</p>
                    </div>
                    <div className="text-center p-3 bg-white/50 dark:bg-slate-800/50 rounded-lg">
                      <p className="text-2xl font-bold">{operationsBrief.executive_summary?.key_metrics?.capacity_utilization}%</p>
                      <p className="text-xs text-muted-foreground">Capacity</p>
                    </div>
                    <div className="text-center p-3 bg-white/50 dark:bg-slate-800/50 rounded-lg">
                      <p className="text-2xl font-bold">{operationsBrief.executive_summary?.key_metrics?.total_teams}</p>
                      <p className="text-xs text-muted-foreground">Teams</p>
                    </div>
                    <div className="text-center p-3 bg-white/50 dark:bg-slate-800/50 rounded-lg">
                      <p className="text-2xl font-bold">{operationsBrief.executive_summary?.key_metrics?.burnout_at_risk}</p>
                      <p className="text-xs text-muted-foreground">At Risk</p>
                    </div>
                  </div>
                </CardContent>
              </ModernCard>

              {/* Monday Questions */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <ModernCard>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Shield className="w-4 h-4 text-green-500" />
                      Are We Operating Safely?
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xl font-bold text-green-600 dark:text-green-400">
                      {operationsBrief.monday_questions?.are_we_operating_safely?.answer}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {operationsBrief.monday_questions?.are_we_operating_safely?.detail}
                    </p>
                  </CardContent>
                </ModernCard>

                <ModernCard>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Flame className="w-4 h-4 text-orange-500" />
                      Who Is At Risk?
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xl font-bold text-orange-600 dark:text-orange-400">
                      {operationsBrief.monday_questions?.who_is_at_risk?.answer}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {operationsBrief.monday_questions?.who_is_at_risk?.detail}
                    </p>
                  </CardContent>
                </ModernCard>

                <ModernCard>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <UserPlus className="w-4 h-4 text-blue-500" />
                      Do We Need To Hire?
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xl font-bold text-blue-600 dark:text-blue-400">
                      {operationsBrief.monday_questions?.do_we_need_to_hire?.answer}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {operationsBrief.monday_questions?.do_we_need_to_hire?.detail}
                    </p>
                  </CardContent>
                </ModernCard>
              </div>

              {/* Simulation Insights */}
              <ModernCard>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Brain className="w-5 h-5 text-violet-500" />
                    AI Simulation Insights
                  </CardTitle>
                  <CardDescription>What-If scenarios from 5 simulations</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 bg-violet-50 dark:bg-violet-900/20 rounded-lg">
                      <p className="text-sm font-medium text-violet-700 dark:text-violet-300">
                        {operationsBrief.simulation_insights?.workforce?.title}
                      </p>
                      <p className="text-lg font-bold mt-1">{operationsBrief.simulation_insights?.workforce?.insight}</p>
                    </div>
                    <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                      <p className="text-sm font-medium text-red-700 dark:text-red-300">
                        {operationsBrief.simulation_insights?.burnout?.title}
                      </p>
                      <p className="text-lg font-bold mt-1">{operationsBrief.simulation_insights?.burnout?.insight}</p>
                    </div>
                    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <p className="text-sm font-medium text-blue-700 dark:text-blue-300">
                        {operationsBrief.simulation_insights?.census?.title}
                      </p>
                      <p className="text-lg font-bold mt-1">{operationsBrief.simulation_insights?.census?.insight}</p>
                    </div>
                    <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                      <p className="text-sm font-medium text-amber-700 dark:text-amber-300">
                        {operationsBrief.simulation_insights?.teams?.title}
                      </p>
                      <p className="text-lg font-bold mt-1">{operationsBrief.simulation_insights?.teams?.insight}</p>
                      {operationsBrief.simulation_insights?.teams?.overloaded?.length > 0 && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Overloaded: {operationsBrief.simulation_insights?.teams?.overloaded?.join(', ')}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </ModernCard>

              {/* Action Items */}
              <ModernCard>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    Prioritized Action Items
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {operationsBrief.action_items?.critical?.length > 0 && (
                      <div>
                        <Badge variant="destructive" className="mb-2">Critical</Badge>
                        {operationsBrief.action_items.critical.map((item, idx) => (
                          <Alert key={idx} variant="destructive" className="mt-2">
                            <AlertTriangle className="w-4 h-4" />
                            <AlertTitle>{item.action}</AlertTitle>
                            <AlertDescription>{item.detail}</AlertDescription>
                          </Alert>
                        ))}
                      </div>
                    )}
                    {operationsBrief.action_items?.high?.length > 0 && (
                      <div>
                        <Badge variant="warning" className="mb-2 bg-orange-500">High Priority</Badge>
                        {operationsBrief.action_items.high.map((item, idx) => (
                          <div key={idx} className="mt-2 p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                            <p className="font-medium">{item.action}</p>
                            <p className="text-sm text-muted-foreground">{item.detail}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    {operationsBrief.action_items?.medium?.length > 0 && (
                      <div>
                        <Badge variant="secondary" className="mb-2">Medium Priority</Badge>
                        {operationsBrief.action_items.medium.map((item, idx) => (
                          <div key={idx} className="mt-2 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                            <p className="font-medium">{item.action}</p>
                            <p className="text-sm text-muted-foreground">{item.detail}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    {!operationsBrief.action_items?.critical?.length && 
                     !operationsBrief.action_items?.high?.length && 
                     !operationsBrief.action_items?.medium?.length && (
                      <div className="text-center py-4">
                        <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-2" />
                        <p className="text-green-600 font-medium">No critical actions required</p>
                        <p className="text-sm text-muted-foreground">Operations are running smoothly</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </ModernCard>
            </div>
          ) : (
            <ModernCard>
              <CardContent className="text-center py-12">
                <Zap className="w-16 h-16 mx-auto text-amber-500 mb-4" />
                <h3 className="text-xl font-semibold mb-2">Generate Monday Operations Brief</h3>
                <p className="text-muted-foreground mb-4">
                  Run all 5 AI simulations to generate a comprehensive operations brief
                </p>
                <Button 
                  onClick={runAllSimulations}
                  disabled={runningAllSims}
                  className="gap-2 bg-gradient-to-r from-amber-500 to-orange-500"
                >
                  {runningAllSims ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Zap className="w-4 h-4" />
                      Generate Operations Brief
                    </>
                  )}
                </Button>
              </CardContent>
            </ModernCard>
          )}
        </TabsContent>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Operational Status */}
            <ModernCard>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-violet-500" />
                  Monday Operational Status
                </CardTitle>
                <CardDescription>Are we operating safely this week?</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {executiveReport?.executive_summary ? (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Operational Risk</span>
                      <Badge variant={executiveReport.executive_summary.operational_risk_level === 'Low' ? 'default' : 'destructive'}>
                        {executiveReport.executive_summary.operational_risk_level}
                      </Badge>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Capacity Utilization</span>
                        <span className="font-medium">{executiveReport.executive_summary.capacity_utilization_percent}%</span>
                      </div>
                      <Progress 
                        value={executiveReport.executive_summary.capacity_utilization_percent} 
                        className="h-2"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4 pt-2">
                      <div className="text-center p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                        <p className="text-2xl font-bold">{executiveReport.executive_summary.total_census}</p>
                        <p className="text-xs text-muted-foreground">Active Census</p>
                      </div>
                      <div className="text-center p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                        <p className="text-2xl font-bold">{executiveReport.executive_summary.total_scheduled_visits}</p>
                        <p className="text-xs text-muted-foreground">Scheduled Visits</p>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Brain className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Run weekly analysis to see operational status</p>
                    <Button variant="outline" size="sm" className="mt-3" onClick={runWeeklyAnalysis}>
                      Generate Report
                    </Button>
                  </div>
                )}
              </CardContent>
            </ModernCard>

            {/* Burnout Risk Summary */}
            <ModernCard>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Flame className="w-5 h-5 text-orange-500" />
                  Who is At Risk?
                </CardTitle>
                <CardDescription>Nurses requiring immediate attention</CardDescription>
              </CardHeader>
              <CardContent>
                {burnoutAlerts?.length > 0 ? (
                  <ScrollArea className="h-[200px]">
                    <div className="space-y-2">
                      {burnoutAlerts.slice(0, 5).map((alert, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className={`w-3 h-3 rounded-full ${getBurnoutColor(alert.burnout_score)}`} />
                            <div>
                              <p className="font-medium">{alert.nurse_name}</p>
                              <p className="text-xs text-muted-foreground">{alert.factors?.[0] || 'Multiple factors'}</p>
                            </div>
                          </div>
                          <Badge variant={getRiskBadge(alert.risk_level)}>
                            Score: {alert.burnout_score}/5
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                ) : (
                  <div className="text-center py-8">
                    <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-3" />
                    <p className="text-green-600 font-medium">All staff within healthy thresholds</p>
                    <p className="text-xs text-muted-foreground mt-1">No burnout alerts this week</p>
                  </div>
                )}
              </CardContent>
            </ModernCard>

            {/* Hiring Predictions */}
            <ModernCard>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-blue-500" />
                  Do We Need to Hire?
                </CardTitle>
                <CardDescription>AI-powered staffing predictions</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {hiringRecommendations ? (
                  <>
                    <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-violet-50 dark:from-blue-900/20 dark:to-violet-900/20 rounded-lg">
                      <div>
                        <p className="text-sm text-muted-foreground">Hiring Urgency</p>
                        <p className="text-xl font-bold capitalize">{hiringRecommendations.hiring_urgency}</p>
                      </div>
                      <Badge 
                        variant={
                          hiringRecommendations.hiring_urgency === 'critical' ? 'destructive' :
                          hiringRecommendations.hiring_urgency === 'high' ? 'warning' : 'default'
                        }
                        className="text-lg px-3 py-1"
                      >
                        {hiringRecommendations.current_capacity}% Capacity
                      </Badge>
                    </div>
                    <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <p className="text-sm font-medium">Recommended Timeline</p>
                      <p className="text-muted-foreground">{hiringRecommendations.recommended_timeline}</p>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <AlertTriangle className="w-4 h-4" />
                      <span>{hiringRecommendations.burnout_correlation} nurses correlated with burnout risk</span>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <TrendingUp className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Run analysis to get hiring predictions</p>
                  </div>
                )}
              </CardContent>
            </ModernCard>

            {/* Team Distribution */}
            <ModernCard>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-green-500" />
                  Team Distribution
                </CardTitle>
                <CardDescription>Capacity by team</CardDescription>
              </CardHeader>
              <CardContent>
                {teamCapacity?.teams?.length > 0 ? (
                  <div className="space-y-3">
                    {teamCapacity.teams.map((team, idx) => (
                      <div key={idx} className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="font-medium">{team.team}</span>
                          <span className={
                            team.capacity_utilization_percent > 90 ? 'text-red-500' :
                            team.capacity_utilization_percent < 70 ? 'text-yellow-500' : 'text-green-500'
                          }>
                            {team.capacity_utilization_percent}%
                          </span>
                        </div>
                        <Progress 
                          value={team.capacity_utilization_percent} 
                          className="h-2"
                        />
                        <p className="text-xs text-muted-foreground">
                          {team.nurse_count} nurses | {team.active_census} patients | {team.total_visits} visits
                        </p>
                      </div>
                    ))}
                    <div className="pt-3 border-t flex justify-between text-sm">
                      <span>Overloaded: <Badge variant="destructive">{teamCapacity.summary?.overloaded || 0}</Badge></span>
                      <span>Optimal: <Badge variant="default">{teamCapacity.summary?.optimal || 0}</Badge></span>
                      <span>Under: <Badge variant="secondary">{teamCapacity.summary?.underutilized || 0}</Badge></span>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No team data available</p>
                  </div>
                )}
              </CardContent>
            </ModernCard>
          </div>
        </TabsContent>

        {/* Predictive Analytics Tab */}
        <TabsContent value="predictive">
          <PredictiveFeatures />
        </TabsContent>

        {/* Nurse Analysis Tab */}
        <TabsContent value="nurses">
          <ModernCard>
            <CardHeader>
              <CardTitle>Nurse Visit Distribution</CardTitle>
              <CardDescription>Detailed analysis of each nurse's workload</CardDescription>
            </CardHeader>
            <CardContent>
              {analysisData?.nurse_analyses?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nurse</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Team</TableHead>
                      <TableHead className="text-right">Caseload</TableHead>
                      <TableHead className="text-right">Weekly Visits</TableHead>
                      <TableHead className="text-right">High-Burden %</TableHead>
                      <TableHead>Burnout Risk</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {analysisData.nurse_analyses.map((nurse, idx) => {
                      const burnout = analysisData.burnout_scores?.find(b => b.nurse_name === nurse.nurse_name);
                      return (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">{nurse.nurse_name}</TableCell>
                          <TableCell>{nurse.role}</TableCell>
                          <TableCell>{nurse.team || '-'}</TableCell>
                          <TableCell className="text-right">{nurse.assigned_caseload}</TableCell>
                          <TableCell className="text-right">
                            <span className={nurse.total_visits > 20 ? 'text-red-500 font-bold' : ''}>
                              {nurse.total_visits}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            <span className={nurse.high_burden_percent > 25 ? 'text-orange-500' : ''}>
                              {nurse.high_burden_percent}%
                            </span>
                          </TableCell>
                          <TableCell>
                            {burnout && (
                              <div className="flex items-center gap-2">
                                <div className={`w-2 h-2 rounded-full ${getBurnoutColor(burnout.burnout_score)}`} />
                                <Badge variant={getRiskBadge(burnout.risk_level)}>
                                  {burnout.risk_level} ({burnout.burnout_score})
                                </Badge>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <Users className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg">No nurse analysis data available</p>
                  <p className="text-sm mt-1">Upload a schedule or run weekly analysis</p>
                </div>
              )}
            </CardContent>
          </ModernCard>
        </TabsContent>

        {/* Teams Tab */}
        <TabsContent value="teams">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {teamCapacity?.teams?.map((team, idx) => (
              <ModernCard key={idx}>
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <CardTitle className="text-lg">{team.team}</CardTitle>
                    <Badge 
                      variant={
                        team.risk_level === 'High' ? 'destructive' :
                        team.risk_level === 'Underutilized' ? 'warning' : 'default'
                      }
                    >
                      {team.risk_level}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Capacity Utilization</span>
                      <span className="font-bold">{team.capacity_utilization_percent}%</span>
                    </div>
                    <Progress value={team.capacity_utilization_percent} className="h-2" />
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded text-center">
                      <p className="font-bold">{team.nurse_count}</p>
                      <p className="text-xs text-muted-foreground">Nurses</p>
                    </div>
                    <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded text-center">
                      <p className="font-bold">{team.active_census}</p>
                      <p className="text-xs text-muted-foreground">Census</p>
                    </div>
                    <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded text-center">
                      <p className="font-bold">{team.total_visits}</p>
                      <p className="text-xs text-muted-foreground">Visits</p>
                    </div>
                    <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded text-center">
                      <p className="font-bold">{team.prn_utilization_percent}%</p>
                      <p className="text-xs text-muted-foreground">PRN Use</p>
                    </div>
                  </div>
                </CardContent>
              </ModernCard>
            ))}
            {(!teamCapacity?.teams || teamCapacity.teams.length === 0) && (
              <div className="col-span-full text-center py-12 text-muted-foreground">
                <Building2 className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg">No team data available</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Recommendations Tab */}
        <TabsContent value="recommendations">
          <ModernCard>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-yellow-500" />
                Strategic Recommendations
              </CardTitle>
              <CardDescription>AI-generated action items for this week</CardDescription>
            </CardHeader>
            <CardContent>
              {analysisData?.recommendations?.length > 0 ? (
                <div className="space-y-4">
                  {analysisData.recommendations.map((rec, idx) => (
                    <Alert key={idx} variant={rec.priority === 'critical' ? 'destructive' : 'default'}>
                      <div className="flex items-start gap-3">
                        {rec.priority === 'critical' ? (
                          <AlertTriangle className="w-5 h-5 text-red-500" />
                        ) : rec.priority === 'high' ? (
                          <AlertCircle className="w-5 h-5 text-orange-500" />
                        ) : (
                          <CheckCircle className="w-5 h-5 text-blue-500" />
                        )}
                        <div className="flex-1">
                          <AlertTitle className="flex items-center gap-2">
                            {rec.title}
                            <Badge variant={rec.priority === 'critical' ? 'destructive' : rec.priority === 'high' ? 'warning' : 'secondary'}>
                              {rec.priority}
                            </Badge>
                          </AlertTitle>
                          <AlertDescription className="mt-2">
                            <p>{rec.description}</p>
                            {rec.affected && (
                              <p className="mt-2 text-sm">
                                <span className="font-medium">Affected:</span> {rec.affected.join(', ')}
                              </p>
                            )}
                            <div className="mt-3 p-2 bg-slate-100 dark:bg-slate-800 rounded flex items-center gap-2">
                              <ArrowRight className="w-4 h-4" />
                              <span className="text-sm font-medium">{rec.action}</span>
                            </div>
                          </AlertDescription>
                        </div>
                      </div>
                    </Alert>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <Zap className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg">No recommendations generated yet</p>
                  <p className="text-sm mt-1">Run weekly analysis to get AI recommendations</p>
                </div>
              )}
            </CardContent>
          </ModernCard>
        </TabsContent>

        {/* AI Insights Tab */}
        <TabsContent value="ai-insights">
          <ModernCard>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Brain className="w-5 h-5 text-violet-500" />
                AI Executive Insights
              </CardTitle>
              <CardDescription>GPT-5.2 powered analysis and predictions</CardDescription>
            </CardHeader>
            <CardContent>
              {executiveReport?.ai_insights?.executive_summary ? (
                <div className="space-y-6">
                  {/* Executive Summary */}
                  <div className="p-4 bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20 rounded-lg">
                    <h3 className="font-semibold mb-2">Executive Summary</h3>
                    <p className="text-sm text-muted-foreground">{executiveReport.ai_insights.executive_summary}</p>
                  </div>

                  {/* Key Insights */}
                  {executiveReport.ai_insights.key_insights?.length > 0 && (
                    <div>
                      <h3 className="font-semibold mb-3">Key Insights</h3>
                      <div className="space-y-2">
                        {executiveReport.ai_insights.key_insights.map((insight, idx) => (
                          <div key={idx} className="flex items-start gap-2 p-3 bg-slate-50 dark:bg-slate-800 rounded">
                            <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                            <span className="text-sm">{insight}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Risk Alerts */}
                  {executiveReport.ai_insights.risk_alerts?.length > 0 && (
                    <div>
                      <h3 className="font-semibold mb-3">Risk Alerts</h3>
                      <div className="space-y-2">
                        {executiveReport.ai_insights.risk_alerts.map((alert, idx) => (
                          <div key={idx} className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 rounded">
                            <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5" />
                            <span className="text-sm">{alert}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Strategic Actions */}
                  {executiveReport.ai_insights.strategic_actions?.length > 0 && (
                    <div>
                      <h3 className="font-semibold mb-3">Strategic Actions</h3>
                      <div className="space-y-2">
                        {executiveReport.ai_insights.strategic_actions.map((action, idx) => (
                          <div key={idx} className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded">
                            <ChevronRight className="w-4 h-4 text-blue-500 mt-0.5" />
                            <span className="text-sm">{action}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <Brain className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg">No AI insights available</p>
                  <p className="text-sm mt-1">Run weekly analysis to generate AI-powered insights</p>
                  <Button variant="outline" size="sm" className="mt-4" onClick={runWeeklyAnalysis}>
                    Generate AI Insights
                  </Button>
                </div>
              )}
            </CardContent>
          </ModernCard>
        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports">
          <ModernCard>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Historical Reports
              </CardTitle>
              <CardDescription>Previously generated CareFlow reports</CardDescription>
            </CardHeader>
            <CardContent>
              {reports?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Census</TableHead>
                      <TableHead>Capacity</TableHead>
                      <TableHead>Risk Level</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reports.map((report, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          {new Date(report.report_date).toLocaleDateString()}
                        </TableCell>
                        <TableCell>{report.report_type}</TableCell>
                        <TableCell>{report.executive_summary?.total_census || '-'}</TableCell>
                        <TableCell>{report.executive_summary?.capacity_utilization_percent || '-'}%</TableCell>
                        <TableCell>
                          <Badge variant={
                            report.executive_summary?.operational_risk_level === 'High' ? 'destructive' :
                            report.executive_summary?.operational_risk_level === 'Moderate' ? 'warning' : 'default'
                          }>
                            {report.executive_summary?.operational_risk_level || '-'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm">
                            <Download className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg">No reports generated yet</p>
                  <p className="text-sm mt-1">Run your first weekly analysis to create a report</p>
                </div>
              )}
            </CardContent>
          </ModernCard>
        </TabsContent>
      </Tabs>
    </div>
  );
}
